import React, { useEffect, useRef, useState } from 'react';
import PatientBlock from 'components/molecules/patientBlock';
import { useTranslation } from 'react-i18next';
import { usePatientContactAvailability } from 'infrastructure/hooks/patient/use-patient-contact-availability';
import { useEdit } from 'infrastructure/providers/edit-provider';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import SlideAnimation from 'components/atoms/base-animations/slide';
import BaseSwitch from 'components/atoms/base-switch';
import useUser from 'utils/useUser';
import BaseButton from 'components/atoms/baseButton';
import DraggableModal from 'components/organisms/draggableModal';

import s from './styles.module.scss';
import ContactAvailabilityUpdateForm from '../update-form';
import ContactAvailabilityViewForm from '../view-form';

import type { IContactAvailabilityUpdateFormRef } from '../update-form';

const formKey = 'ContactAvailabilityForm';

interface IProps {
  patientGuid: string;
  readOnly?: boolean;
  mode?: 'banner' | 'profile';
  disableBannerEditBtn?: boolean;
  headerAddon?: React.ReactNode;
}

const ContactAvailabilityCombinedForm: React.FC<IProps> = (props) => {
  const {
    patientGuid,
    readOnly = false,
    mode = 'profile',
    disableBannerEditBtn,
    headerAddon,
  } = props;

  const { t } = useTranslation();
  const { isMobileDevice } = useUser();

  const { canEdit, setEdit } = useEdit();

  const [anytime, setAnytime] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { loading, availabilities } = usePatientContactAvailability({
    patientGuid,
  });

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const updateFormRef = useRef<IContactAvailabilityUpdateFormRef>(null);

  const onEdit = () => {
    setIsEdit(true);
    setEdit(true, formKey);
  };

  const onCancel = () => {
    setIsEdit(false);
    setEdit(false);
    setAnytime(!availabilities.length);
  };

  const onSaved = () => {
    setIsEdit(false);
    setEdit(false);
  };

  const onSave = async () => {
    if (updateFormRef.current) {
      setIsSaving(true);
      await updateFormRef.current.save().finally(() => {
        setIsSaving(false);
      });
    }
  };

  const additionalControls = (
    <BaseSwitch
      disabled={loading || isSaving}
      className={s.label}
      dataCy="contactAvailability-anytime-switch"
      label={t('labels.anytime')}
      value={anytime}
      onChange={setAnytime}
    />
  );

  const modalTitle = (
    <div
      className={s['modal-title-wrapper']}
      data-cy="contactAvailability-modal-title"
    >
      <h5 data-draggable>{t('labels.contactAvailability')}</h5>
      {additionalControls}
    </div>
  );

  const isBannerMode = mode === 'banner';
  const isProfileMode = mode === 'profile';
  const collapseAnimationInitialHeight = isMobileDevice ? 360 : 120;

  useEffect(() => {
    setAnytime(!availabilities.length);
  }, [availabilities.length]);

  return (
    <PatientBlock
      title={t('labels.contactAvailability')}
      isEdit={isEdit}
      onEdit={onEdit}
      onSave={onSave}
      onCancel={onCancel}
      loading={isBannerMode ? isSaving : loading || isSaving}
      canEdit={isProfileMode && !readOnly}
      disabled={!canEdit(formKey)}
      additionalControls={additionalControls}
      mobileTwoHeaderRows
      headerAddon={headerAddon}
      spinning={false}
    >
      <SlideAnimation
        uniqueKey={`${isEdit}`}
        // eslint-disable-next-line no-nested-ternary
        direction={isEdit ? (isMobileDevice ? 'left' : 'right') : 'left'}
        disableAnimation={isBannerMode}
      >
        <CollapseAnimation
          initialHeight={collapseAnimationInitialHeight}
          opened={isEdit}
          uniqueKey={`${loading}`}
          disableAnimation={isBannerMode}
        >
          {isEdit && !readOnly && isProfileMode ? (
            <ContactAvailabilityUpdateForm
              ref={updateFormRef}
              patientGuid={patientGuid}
              onSaved={onSaved}
              anytime={anytime}
            />
          ) : (
            <ContactAvailabilityViewForm
              patientGuid={patientGuid}
              withEditBtn={isBannerMode}
              onEditClick={onEdit}
              disableEditBtn={disableBannerEditBtn}
            />
          )}
        </CollapseAnimation>
      </SlideAnimation>
      {isEdit && !readOnly && isBannerMode && (
        <DraggableModal
          title={modalTitle}
          isOpen={isEdit}
          toggle={onCancel}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          footer={
            <>
              <BaseButton
                dataCy="contactAvailability-cancel-button"
                key="cancel"
                type="secondary"
                label={t('controls.cancel')}
                onClick={onCancel}
              />
              <BaseButton
                dataCy="contactAvailability-save-button"
                key="save"
                type="primary"
                label={t('controls.save')}
                loading={isSaving}
                onClick={onSave}
              />
            </>
          }
        >
          <ContactAvailabilityUpdateForm
            ref={updateFormRef}
            patientGuid={patientGuid}
            onSaved={onSaved}
            anytime={anytime}
          />
        </DraggableModal>
      )}
    </PatientBlock>
  );
};

export default ContactAvailabilityCombinedForm;
